import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WatermarkedMedia } from '@/components/WatermarkedMedia';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Image, Play, BookOpen, Sparkles, Film, Layers, Wand2, X, Download, Share2, Lock, Trash2, AlertTriangle, Maximize2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { toast } from 'sonner';
import { CinegraphicPlayer } from '@/components/CinegraphicPlayer';

function ContentCard({ item, onClick, isAdmin, onDelete }) {
  const isVideo = item.content_type === 'cinegraphic' || item.file_url?.match(/\.(mp4|webm|mov)$/i);
  const overlay = item.overlay || {};

  return (
    <Card className="rounded-2xl border-slate-100 overflow-hidden hover:shadow-lg hover:border-blue-200 hover:-translate-y-0.5 transition-all cursor-pointer group relative"
      data-testid={`content-card-${item.content_id}`} onClick={() => onClick(item)}>
      {isAdmin && (
        <button data-testid={`content-delete-${item.content_id}`} onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          title="Delete (Admin only)"
          className="absolute top-2.5 left-2.5 z-10 w-8 h-8 rounded-full bg-white/95 hover:bg-red-500 text-red-500 hover:text-white shadow-md flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      <div className={`relative ${isVideo ? 'bg-purple-50' : 'bg-blue-50'} aspect-video flex items-center justify-center overflow-hidden`}>
        {item.file_url && !isVideo ? (
          <>
            <img src={item.file_url} alt={item.title} className="w-full h-full object-cover" />
            {(overlay.title || item.title) && (
              <div className="absolute inset-x-0 top-0 p-2.5 bg-gradient-to-b from-slate-900/70 to-transparent text-white pointer-events-none">
                <p className="font-heading text-[11px] font-semibold line-clamp-1 drop-shadow">{overlay.title || item.title}</p>
              </div>
            )}
          </>
        ) : item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" />
        ) : isVideo ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-7 h-7 text-purple-500 ml-0.5" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Image className="w-10 h-10 text-blue-400 opacity-50" />
          </div>
        )}
        <Badge className={`absolute top-2.5 right-2.5 ${isVideo ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'} text-[10px] font-heading`}>
          {isVideo ? 'Video' : 'Visual'}
        </Badge>
      </div>
      <div className="p-4">
        <h4 className="font-heading text-sm font-semibold text-slate-800 mb-1 line-clamp-2">{item.title}</h4>
        {item.description && <p className="text-xs text-slate-400 font-body line-clamp-2 mb-2">{item.description}</p>}
        {item.topic && <span className="text-[10px] text-slate-400 font-body">{item.topic}</span>}
      </div>
    </Card>
  );
}

function InfographicPreviewModal({ item, onClose, user }) {
  const modalContentRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hideOverlay, setHideOverlay] = useState(false);

  // Iter 56: auto-hide the top title overlay after 3.5s so the student can
  // see the full infographic without hindrance. Overlay reappears on hover.
  useEffect(() => {
    if (!item) return;
    setHideOverlay(false);
    const t = setTimeout(() => setHideOverlay(true), 3500);
    return () => clearTimeout(t);
  }, [item]);

  // Cross-browser fullscreen toggle for the infographic image.
  const toggleFullscreen = () => {
    const el = imageWrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req) req.call(el).catch(() => {});
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      if (exit) exit.call(document).catch(() => {});
    }
  };
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  if (!item) return null;
  const overlay = item.overlay || {};
  const explanation = item.explanation || {};
  const title = overlay.title || item.title;
  const subtitle = overlay.subtitle || `${item.subject || ''} ${item.topic ? '· ' + item.topic : ''}`.trim();
  const isVideo = item.content_type === 'cinegraphic' || item.file_url?.match(/\.(mp4|webm|mov)$/i);
  const downloadUrl = item.file_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.file_url}` : item.file_url;
  const hasExplanation = explanation && (explanation.core_idea || (explanation.key_points && explanation.key_points.length));

  const canShare = user?.role === 'admin' || ['growth', 'premium', 'elite'].includes(user?.subscription_tier);

  const buildShareCard = async () => {
    // Compose shareable card on canvas: image + branded watermark
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const size = 1080;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          // Cream background
          ctx.fillStyle = '#FFF7ED';
          ctx.fillRect(0, 0, size, size);
          // Draw infographic (contain in top 70%)
          const imgH = Math.floor(size * 0.72);
          const ratio = Math.min(size / img.width, imgH / img.height);
          const drawW = img.width * ratio;
          const drawH = img.height * ratio;
          const offsetX = (size - drawW) / 2;
          const offsetY = 40;
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(40, 20, size - 80, imgH + 40);
          ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
          // Title band
          ctx.fillStyle = '#0F172A';
          ctx.font = 'bold 54px Inter, system-ui, sans-serif';
          ctx.textAlign = 'center';
          const titleY = imgH + 130;
          const truncTitle = title.length > 48 ? title.slice(0, 48) + '…' : title;
          ctx.fillText(truncTitle, size / 2, titleY);
          // Subtitle
          ctx.fillStyle = '#64748B';
          ctx.font = '32px Inter, system-ui, sans-serif';
          ctx.fillText(subtitle || '', size / 2, titleY + 50);
          // Watermark brand bar
          const barY = size - 90;
          const grad = ctx.createLinearGradient(0, barY, size, barY);
          grad.addColorStop(0, '#6366F1');
          grad.addColorStop(0.5, '#A855F7');
          grad.addColorStop(1, '#F59E0B');
          ctx.fillStyle = grad;
          ctx.fillRect(0, barY, size, 90);
          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 38px Inter, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Gurukul Veda · Your Personal Study Companion', size / 2, barY + 58);
          canvas.toBlob(blob => resolve(blob), 'image/png', 0.95);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = downloadUrl;
    });
  };

  const handleShare = async () => {
    if (!canShare) {
      toast.error('Share is a Growth feature. Upgrade to share your mini-lessons with friends.');
      return;
    }
    try {
      toast.info('Creating shareable card with watermark…');
      const blob = await buildShareCard();
      if (!blob) throw new Error('Card render failed');
      const file = new File([blob], `gurukul-veda-${(title || 'lesson').toLowerCase().replace(/\s+/g, '-').slice(0, 40)}.png`, { type: 'image/png' });

      // Use Web Share API where available (mobile + modern desktop)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${title} · Gurukul Veda`,
            text: `Just learned about ${title} on Gurukul Veda — check this out!`,
          });
          toast.success('Shared!');
          return;
        } catch (shareErr) {
          // User cancelled or share failed — fall through to download
          if (shareErr?.name !== 'AbortError') console.warn('Share API failed, falling back to download', shareErr);
        }
      }
      // Fallback: trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Shareable card downloaded. Post it on WhatsApp or Instagram!');
    } catch (err) {
      console.error(err);
      toast.error('Could not create share card. Try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
      data-testid="infographic-preview-modal" onClick={onClose}>
      <div className="relative max-w-6xl w-full max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} data-testid="preview-close-btn"
          className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all">
          <X className="w-5 h-5" />
        </button>
        {/* Download button — admin / support_staff ONLY. Students must
            consume content through GurukulVeda; no local copies. */}
        {(user?.role === 'admin' || user?.role === 'support_staff') && (
          <a href={downloadUrl} download target="_blank" rel="noreferrer" data-testid="preview-download-btn"
            className="absolute top-3 right-16 z-20 px-3 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 text-white text-xs font-heading transition-all">
            <Download className="w-4 h-4" /> Download
          </a>
        )}
        {/* Share button — Growth/Premium only, downloads or shares branded card with watermark */}
        <button onClick={handleShare} data-testid="preview-share-btn"
          title={canShare ? 'Share with watermark (Growth feature)' : 'Upgrade to Growth to share'}
          className={`absolute top-3 right-36 z-20 px-3 h-10 rounded-full ${canShare ? 'bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600' : 'bg-white/10 hover:bg-white/20'} flex items-center gap-2 text-white text-xs font-heading transition-all`}>
          {canShare ? <Share2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Share
        </button>
        {/* Fullscreen — image only. Videos already have a fullscreen control
            in the CinegraphicPlayer chrome. */}
        {!isVideo && (
          <button onClick={toggleFullscreen} data-testid="preview-fullscreen-btn"
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'View fullscreen'}
            className="absolute top-3 right-[13rem] z-20 px-3 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 text-white text-xs font-heading transition-all">
            <Maximize2 className="w-4 h-4" /> {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
        )}

        <div className={`grid ${hasExplanation ? 'md:grid-cols-5' : 'grid-cols-1'} gap-4 items-start`}>
          {/* Visual with overlaid captions */}
          <div ref={imageWrapperRef}
            onMouseEnter={() => setHideOverlay(false)}
            onMouseLeave={() => setHideOverlay(true)}
            className={`relative rounded-3xl overflow-hidden shadow-2xl bg-white ${hasExplanation ? 'md:col-span-3' : ''}`}>
            {isVideo ? (
              <WatermarkedMedia>
                <CinegraphicPlayer
                  src={item.file_url}
                  contentId={item.content_id}
                  captionsUrl={item.captions_url || null}
                  poster={item.thumbnail_url || null}
                  data-testid="preview-video"
                />
              </WatermarkedMedia>
            ) : (
              <WatermarkedMedia>
                <img src={item.file_url} alt={title} className="w-full h-auto block" data-testid="preview-image" />
              </WatermarkedMedia>
            )}
            {/* HTML overlay captions — 100% text accuracy. Suppressed for videos
                so they don't visually cover the player's bottom control bar.
                Iter 56: auto-hides after 3.5s so the top of the image isn't
                permanently covered; reappears on mouse hover. */}
            {!isVideo && (
              <div className={`absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-slate-900/80 via-slate-900/30 to-transparent text-white pointer-events-none transition-opacity duration-500 ${hideOverlay ? 'opacity-0' : 'opacity-100'}`}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold drop-shadow-lg leading-tight" data-testid="preview-overlay-title">{title}</h2>
                {subtitle && <p className="font-body text-sm text-white/90 mt-1 drop-shadow" data-testid="preview-overlay-subtitle">{subtitle}</p>}
              </div>
            )}
            {!isVideo && explanation.one_line_hook && (
              <div className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent text-white pointer-events-none transition-opacity duration-500 ${hideOverlay ? 'opacity-0' : 'opacity-100'}`}>
                <p className="font-heading italic text-sm sm:text-base leading-relaxed drop-shadow" data-testid="preview-overlay-hook">
                  "{explanation.one_line_hook}"
                </p>
              </div>
            )}
          </div>

          {/* Structured Explanation Panel */}
          {hasExplanation && (
            <div className="md:col-span-2 rounded-3xl bg-white shadow-2xl p-6 max-h-[88vh] overflow-y-auto" data-testid="preview-explanation-panel">
              {/* Core Idea */}
              {explanation.core_idea && (
                <div className="mb-5">
                  <h3 className="text-[10px] uppercase tracking-wider font-heading text-indigo-500 mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Core Idea
                  </h3>
                  <p className="text-sm text-slate-700 font-body leading-relaxed" data-testid="preview-core-idea">{explanation.core_idea}</p>
                </div>
              )}

              {/* Key Points */}
              {explanation.key_points && explanation.key_points.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-[10px] uppercase tracking-wider font-heading text-emerald-600 mb-2">Key Points</h3>
                  <ul className="space-y-2" data-testid="preview-key-points">
                    {explanation.key_points.map((pt, i) => (
                      <li key={i} className="text-sm text-slate-700 font-body leading-relaxed flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-heading font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Real-life Example */}
              {explanation.real_life_example && (
                <div className="mb-5 p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <h3 className="text-[10px] uppercase tracking-wider font-heading text-amber-700 mb-1.5">Real-life Example</h3>
                  <p className="text-sm text-amber-900 font-body leading-relaxed" data-testid="preview-real-life">{explanation.real_life_example}</p>
                </div>
              )}

              {/* Memory Tip */}
              {explanation.memory_tip && (
                <div className="p-3 rounded-xl bg-purple-50 border border-purple-100">
                  <h3 className="text-[10px] uppercase tracking-wider font-heading text-purple-700 mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Memory Tip
                  </h3>
                  <p className="text-sm text-purple-900 font-body leading-relaxed" data-testid="preview-memory-tip">{explanation.memory_tip}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ContentGallery({ activeSubject, activeTopic: headerTopic }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [headings, setHeadings] = useState([]);
  const [selectedHeading, setSelectedHeading] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [generating, setGenerating] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [coverage, setCoverage] = useState({ infographic: false, cinegraphic: false, staff_count: 0 });

  // Iter 56 (Feb 2026): Admin bulk-controls bar auto-collapses to a small
  // floating pill after a few seconds. Was covering the top ~15% of the
  // infographic cards. Admin can re-expand any time by clicking the pill.
  const [adminBarExpanded, setAdminBarExpanded] = useState(true);
  const adminBarTimerRef = useRef(null);
  useEffect(() => {
    if (adminBarTimerRef.current) clearTimeout(adminBarTimerRef.current);
    if (adminBarExpanded) {
      adminBarTimerRef.current = setTimeout(() => setAdminBarExpanded(false), 5000);
    }
    return () => { if (adminBarTimerRef.current) clearTimeout(adminBarTimerRef.current); };
  }, [adminBarExpanded, activeTab, items.length]);
  const [confirmDelete, setConfirmDelete] = useState(null); // { mode: 'single'|'bulk', item?, scope? }

  const subject = activeSubject || '';
  const topic = headerTopic || '';

  // Fetch headings when topic changes
  useEffect(() => {
    if (topic && user?.board) {
      apiGet(`/education/headings?topic=${encodeURIComponent(topic)}&subject=${encodeURIComponent(subject)}&board=${encodeURIComponent(user.board)}&class_level=${user.class_level || ''}`)
        .then(d => { setHeadings(d.headings || []); setSelectedHeading(''); })
        .catch(() => setHeadings([]));
    } else {
      setHeadings([]);
      setSelectedHeading('');
    }
  }, [topic, subject, user?.board, user?.class_level]);

  // Fetch content based on filters.
  // BUG FIX (2026-02, iter 53): Skip the API call entirely until BOTH subject
  // and topic are known. Previously the effect fired twice on initial mount:
  //   (1) with topic="" → the backend returned every item in the subject
  //       (subject-only fuzzy match), producing a flash of unrelated content
  //   (2) after WebLayout auto-picked the first topic → the correct empty/
  //       populated result. The flash-then-blank was visible for ~200ms and
  //       badly confused students (they saw a card belonging to another
  //       chapter appear and vanish).
  useEffect(() => {
    if (!subject || !topic) {
      setItems([]);
      setLoading(false);
      return;
    }
    const params = new URLSearchParams();
    // Always fetch ALL content types so the tab counts stay stable; the
    // active tab only filters what's DISPLAYED (client-side via displayItems).
    params.set('subject', subject);
    params.set('topic', selectedHeading || topic);
    if (user?.board) params.set('board', user.board);
    if (user?.class_level) params.set('class_level', user.class_level);

    let cancelled = false;
    setLoading(true);
    apiGet(`/content/library?${params.toString()}`).then(d => {
      if (cancelled) return;
      setItems(d.items || []);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subject, topic, selectedHeading, user?.board, user?.class_level]);

  // Fetch coverage flag — drives visibility of the "Generate Infographic"
  // button vs the "Curated content available" pill. Bug fix (2026-02):
  // previously the coverage state was hard-coded to `false`, so the
  // Generate button was always shown and approved/cached content in the
  // Content Library appeared as if it didn't exist to students.
  // A `cancelled` guard prevents a stale response from a previous
  // subject/topic pair from overwriting a newer one when the user
  // switches chapters quickly.
  useEffect(() => {
    let cancelled = false;
    if (!subject || !topic) {
      setCoverage({ infographic: false, cinegraphic: false, staff_count: 0 });
      return () => { cancelled = true; };
    }
    const params = new URLSearchParams();
    params.set('subject', subject);
    params.set('topic', selectedHeading || topic);
    if (selectedHeading) params.set('heading', selectedHeading);
    if (user?.board) params.set('board', user.board);
    if (user?.class_level) params.set('class_level', user.class_level);
    apiGet(`/content/coverage?${params.toString()}`)
      .then(d => {
        if (cancelled) return;
        setCoverage({
          infographic: !!d.infographic,
          cinegraphic: !!d.cinegraphic,
          staff_count: d.staff_count || 0,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCoverage({ infographic: false, cinegraphic: false, staff_count: 0 });
      });
    return () => { cancelled = true; };
  }, [subject, topic, selectedHeading, user?.board, user?.class_level]);

  const infographics = items.filter(i => i.content_type === 'infographic');
  const cinegraphics = items.filter(i => i.content_type === 'cinegraphic');
  const displayItems = activeTab === 'infographics' ? infographics : activeTab === 'cinegraphics' ? cinegraphics : items;

  const handleGenerateInfographic = async () => {
    if (!subject || !topic) { toast.error('Select a subject and chapter first'); return; }
    setGenerating(true);
    const buddyName = user?.study_buddy_name || 'Your Study Buddy';
    toast.info(`${buddyName} is generating the right kind of Graphics for you… allow a minute please.`);
    try {
      const res = await apiPost('/content/generate-infographic', {
        subject, topic, heading: selectedHeading, board: user?.board || '', class_level: user?.class_level || ''
      });
      toast.success(`${buddyName} is ready with a fresh visual for you!`);
      // Refresh content list
      const params = new URLSearchParams();
      if (subject) params.set('subject', subject);
      if (topic) params.set('topic', selectedHeading || topic);
      if (user?.board) params.set('board', user.board);
      if (user?.class_level) params.set('class_level', user.class_level);
      const d = await apiGet(`/content/library?${params.toString()}`);
      setItems(d.items || []);
    } catch (e) { toast.error(e.message || 'Generation failed'); }
    setGenerating(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-12" data-testid="content-gallery">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-500" /> Graphics
        </h1>
        <p className="text-sm text-slate-500 font-body mt-1">
          {subject && topic ? `${subject} · ${topic}` : 'Select a subject and chapter from the header to explore content'}
        </p>
      </div>

      {/* Heading Dropdown */}
      {headings.length > 0 && (
        <div className="mb-5">
          <select data-testid="heading-filter" value={selectedHeading} onChange={e => setSelectedHeading(e.target.value)}
            className="w-full sm:w-auto px-4 py-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none min-w-[260px]">
            <option value="">All Headings in {topic}</option>
            {headings.map(h => (
              <option key={h.heading_id || h.name} value={h.name}>{h.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Generate Infographic Button — hidden if curated content already exists.
          Only visible to non-curators (students) so admins don't accidentally
          burn LLM budget on the student-only flow. */}
      {subject && topic && !coverage.infographic && !isAdmin && (
        <div className="mb-5 flex items-center gap-3" data-testid="generate-infographic-panel">
          <Button data-testid="generate-infographic-btn" onClick={handleGenerateInfographic} disabled={generating}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-heading text-sm gap-2 shadow-lg shadow-amber-500/20">
            {generating ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {(user?.study_buddy_name || 'Study Buddy')} is on it…</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Ask {user?.study_buddy_name || 'my Study Buddy'} for a visual</>
            )}
          </Button>
          {generating && (
            <span className="text-xs text-slate-500 font-body italic" data-testid="buddy-loading-hint">
              {user?.study_buddy_name || 'Your Study Buddy'} is generating the right kind of Graphics for you — allow a minute please ✨
            </span>
          )}
        </div>
      )}
      {/* Iter 56: The "Curated content available" pill was removed — this
          was an internal debugging affordance that leaked to students. The
          student's UX now shows either the Generate button (no content yet)
          or the actual content grid (available). No middle-man banner. */}

      {/* Tabs: All / Infographics / Cinegraphics */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 rounded-xl p-1 mb-6 w-fit" data-testid="graphics-tabs">
          <TabsTrigger value="all" className="rounded-lg px-5 py-2 text-xs font-heading data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> All ({items.length})
          </TabsTrigger>
          <TabsTrigger value="infographics" className="rounded-lg px-5 py-2 text-xs font-heading data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Image className="w-3.5 h-3.5 mr-1.5" /> Infographics ({infographics.length})
          </TabsTrigger>
          <TabsTrigger value="cinegraphics" className="rounded-lg px-5 py-2 text-xs font-heading data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Film className="w-3.5 h-3.5 mr-1.5" /> Cinegraphics ({cinegraphics.length})
          </TabsTrigger>
        </TabsList>

        {/* Content Grid */}
        {['all', 'infographics', 'cinegraphics'].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : displayItems.length === 0 ? (
              <Card className="p-10 rounded-2xl border-slate-100 text-center">
                {tabKey === 'infographics' ? (
                  <Image className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                ) : tabKey === 'cinegraphics' ? (
                  <Film className="w-12 h-12 text-purple-200 mx-auto mb-4" />
                ) : (
                  <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                )}
                <h3 className="font-heading text-base font-semibold text-slate-600 mb-2">
                  {!subject ? 'Select a Subject' : !topic ? 'Select a Chapter' : 'No Content Yet'}
                </h3>
                <p className="text-sm text-slate-400 font-body">
                  {!subject || !topic
                    ? 'Use the Subject and Chapter dropdowns in the header to browse graphics.'
                    : `${tabKey === 'infographics' ? 'Infographic' : tabKey === 'cinegraphics' ? 'Cinegraphic' : 'Visual'} content for this topic will appear here as our team adds it.`
                  }
                </p>
              </Card>
            ) : (
              <div>
                {/* Admin: bulk delete controls — auto-collapses after 5s so the
                    infographic images below aren't obscured. Click the pill
                    to re-expand. */}
                {isAdmin && displayItems.length > 0 && (
                  adminBarExpanded ? (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-red-50/40 border border-red-100 animate-fade-in" data-testid="admin-bulk-controls">
                      <div className="flex items-center gap-2 text-xs text-red-700 font-body">
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Admin controls · {displayItems.length} item(s) in view</span>
                        <span title="These buttons let staff bulk-clean AI-generated drafts or every asset visible on this page (respects the current subject/chapter filters). Students don't see this bar."
                          data-testid="admin-bulk-controls-help"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/50 text-[10px] text-red-500 cursor-help">
                          <Info className="w-2.5 h-2.5" /> what&apos;s this?
                        </span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <button data-testid="admin-clear-ai-btn" onClick={() => setConfirmDelete({ mode: 'bulk', scope: 'ai_only', label: 'AI-generated content in this view' })}
                          className="px-3 py-1.5 rounded-full text-[11px] font-heading bg-white border border-red-200 text-red-600 hover:bg-red-500 hover:text-white transition-colors">
                          Clear AI-generated
                        </button>
                        <button data-testid="admin-clear-all-btn" onClick={() => setConfirmDelete({ mode: 'bulk', scope: 'all', label: 'ALL content in this view' })}
                          className="px-3 py-1.5 rounded-full text-[11px] font-heading bg-red-500 text-white hover:bg-red-600 transition-colors">
                          Clear All in View
                        </button>
                        <button onClick={() => setAdminBarExpanded(false)}
                          title="Collapse now"
                          data-testid="admin-bulk-collapse-btn"
                          className="p-1.5 rounded-full text-red-500 hover:bg-red-100 transition-colors">
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setAdminBarExpanded(true)}
                      data-testid="admin-bulk-controls-collapsed"
                      title="Expand admin controls"
                      className="mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 border border-red-100 text-[11px] font-heading text-red-600 transition-colors animate-fade-in">
                      <Trash2 className="w-3 h-3" />
                      Admin controls · {displayItems.length}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  )
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {displayItems.map(item => <ContentCard key={item.content_id} item={item} onClick={(it) => {
                    setPreviewItem(it);
                    // Iter 60w — fire-and-forget view tracking. Skip when
                    // an admin/staff is browsing so their curation clicks
                    // don't inflate student engagement counters.
                    if (!isAdmin && it?.content_id) {
                      apiPost(`/content/${it.content_id}/view`, {}).catch(() => {});
                    }
                  }}
                    isAdmin={isAdmin} onDelete={(it) => setConfirmDelete({ mode: 'single', item: it })} />)}
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {previewItem && <InfographicPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} user={user} />}

      {/* Admin delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="delete-confirm-modal"
          onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
              {confirmDelete.mode === 'single' ? 'Delete this item?' : 'Delete in bulk?'}
            </h3>
            <p className="text-sm text-slate-500 font-body mb-5">
              {confirmDelete.mode === 'single'
                ? `"${confirmDelete.item?.title}" will be permanently removed from the gallery and disk.`
                : `${confirmDelete.label} will be permanently removed from the gallery and disk. This cannot be undone.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)} data-testid="delete-cancel-btn"
                className="px-4 py-2 rounded-full text-sm font-heading bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
                Cancel
              </button>
              <button data-testid="delete-confirm-btn" onClick={async () => {
                try {
                  if (confirmDelete.mode === 'single') {
                    await apiDelete(`/content/${confirmDelete.item.content_id}`);
                    toast.success('Item deleted');
                  } else {
                    const payload = {
                      uploaded_by_scope: confirmDelete.scope,
                    };
                    if (activeTab === 'infographics') payload.content_type = 'infographic';
                    else if (activeTab === 'cinegraphics') payload.content_type = 'cinegraphic';
                    if (subject) payload.subject = subject;
                    if (topic) payload.topic = selectedHeading || topic;
                    if (user?.board) payload.board = user.board;
                    if (user?.class_level) payload.class_level = user.class_level;
                    const res = await apiPost('/content/bulk-delete', payload);
                    toast.success(`Deleted ${res.deleted_count} item(s)`);
                  }
                  // Refresh list
                  const params = new URLSearchParams();
                  if (activeTab !== 'all') params.set('content_type', activeTab === 'infographics' ? 'infographic' : 'cinegraphic');
                  if (subject) params.set('subject', subject);
                  if (topic) params.set('topic', selectedHeading || topic);
                  if (user?.board) params.set('board', user.board);
                  if (user?.class_level) params.set('class_level', user.class_level);
                  const d = await apiGet(`/content/library?${params.toString()}`);
                  setItems(d.items || []);
                } catch (e) {
                  toast.error('Delete failed: ' + (e.message || 'unknown error'));
                } finally {
                  setConfirmDelete(null);
                }
              }}
                className="px-4 py-2 rounded-full text-sm font-heading bg-red-500 text-white hover:bg-red-600 transition-colors">
                {confirmDelete.mode === 'single' ? 'Delete' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
